import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface CategoryGridProps {
  section: any;
}

export default function CategoryGrid({ section }: CategoryGridProps) {
  const categories: any[] = section.categories ?? [];
  const headingId = `categories-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  if (categories.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-[--bg-warm]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
          >
            {isActive ? (
              <QuickEditInline
                value={section.title || 'Browse Categories'}
                onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                label="Categories title"
                as="span"
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              />
            ) : (section.title || 'Browse Categories')}
          </h2>
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {isActive ? (
                <QuickEditInline
                  value={section.subtitle}
                  onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                  label="Categories subtitle"
                  as="span"
                  className="text-sage-600"
                />
              ) : section.subtitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {categories.map((cat: any) => {
            const imageUrl = cat.image_url;

            return (
              <Link
                key={cat.id}
                to={`/shop/${cat.slug}`}
                className={cn(
                  'group relative overflow-hidden rounded-xl',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2'
                )}
              >
                {imageUrl ? (
                  <div className="aspect-[3/4] relative">
                    <img
                      src={imageUrl}
                      alt={cat.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-900/70 via-sage-900/20 to-transparent" aria-hidden="true" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-display text-lg font-bold text-white">
                        {cat.name}
                      </h3>
                      {cat.product_count != null && (
                        <p className="mt-0.5 text-sm text-sage-200">
                          {cat.product_count} {cat.product_count === 1 ? 'product' : 'products'}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[3/4] flex flex-col items-center justify-center gap-3 bg-sage-50 border border-sage-200 rounded-xl p-6 text-center transition-all group-hover:border-sage-300 group-hover:shadow-md">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-sage-100 text-sage-600">
                      <Package className="h-8 w-8" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-sage-900">{cat.name}</h3>
                      {cat.product_count != null && (
                        <p className="mt-0.5 text-sm text-sage-500">
                          {cat.product_count} {cat.product_count === 1 ? 'product' : 'products'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
